// Site Styles Configuration
// Provides a simple, extensible site styles system starting with a Light style.
// Future: add a proper Dark style and expand variables as needed.

export const SiteStyleIds = {
  LIGHT: 'light',
  DARK: 'dark',
};

const siteStyles = {
  [SiteStyleIds.LIGHT]: {
    id: SiteStyleIds.LIGHT,
    name: 'Light',
    colors: {
      main: '#ffffff',
      sub: '#000000',
      canvasBackground: '#ffffff',
      text: '#111111',
      border: '#e5e7eb',
      muted: '#f5f6f7',
      accent: '#007bff',
      accentSoft: '#e7f1ff',
      onAccent: '#ffffff',
      // Provide a light hover background distinct from main to improve contrast
      hoverBg: '#f3f4f6',
    },
  },
  [SiteStyleIds.DARK]: {
    id: SiteStyleIds.DARK,
    name: 'Dark (experimental)',
    colors: {
      // Use a dark gray for the root background to avoid pure black
      main: '#0f1115',
      sub: '#ffffff',
      canvasBackground: '#ffffff',
      text: '#f5f5f5',
      border: '#2a2a2a',
      muted: '#141414',
      accent: '#4da3ff',
      accentSoft: '#10263a',
      onAccent: '#ffffff',
      // Provide explicit sidebar/hover backgrounds for better theming control
      sidebarBg: '#12161d',
      hoverBg: '#222735',
    },
  },
};

// Persisted style selection (localStorage key)
// Preferred key uses 'app.Site.id'; we still read/write compatibility & legacy keys
const STORAGE_KEY = 'app.Site.id';

export function getActiveSiteStyleId() {
  try {
    // Priority (new -> compat -> legacy):
    // URL: ?Site= -> ?SiteStyles= -> ?site_styles= -> ?theme=
    // Storage: app.Site.id -> app.SiteStyles.id -> app.site_styles.id -> app.theme.id
    const params = new URLSearchParams(window.location.search);
    const fromUrlNew = params.get('Site');
    if (fromUrlNew && siteStyles[fromUrlNew]) return fromUrlNew;

    const fromUrlCompat = params.get('SiteStyles');
    if (fromUrlCompat && siteStyles[fromUrlCompat]) return fromUrlCompat;

    const fromUrlLegacy1 = params.get('site_styles');
    if (fromUrlLegacy1 && siteStyles[fromUrlLegacy1]) return fromUrlLegacy1;

    const fromUrlLegacy2 = params.get('theme');
    if (fromUrlLegacy2 && siteStyles[fromUrlLegacy2]) return fromUrlLegacy2;

    const savedNew = localStorage.getItem(STORAGE_KEY); // app.Site.id
    if (savedNew && siteStyles[savedNew]) return savedNew;

    const savedCompat = localStorage.getItem('app.SiteStyles.id');
    if (savedCompat && siteStyles[savedCompat]) return savedCompat;

    const savedLegacy1 = localStorage.getItem('app.site_styles.id');
    if (savedLegacy1 && siteStyles[savedLegacy1]) return savedLegacy1;

    const savedLegacy2 = localStorage.getItem('app.theme.id');
    if (savedLegacy2 && siteStyles[savedLegacy2]) return savedLegacy2;
  } catch (_) {}
  return SiteStyleIds.LIGHT;
}

export function setActiveSiteStyleId(styleId) {
  if (!siteStyles[styleId]) return;
  try {
    // Write to new, compatibility, and legacy keys for smooth migration
    localStorage.setItem(STORAGE_KEY, styleId);            // app.Site.id (new)
    localStorage.setItem('app.SiteStyles.id', styleId);    // compatibility
    localStorage.setItem('app.site_styles.id', styleId);   // legacy
    localStorage.setItem('app.theme.id', styleId);         // legacy
    // Apply styles immediately
    applySiteStylesToDocument();
  } catch (_) {}
}

export function getActiveSiteStyle() {
  const id = getActiveSiteStyleId();
  return siteStyles[id] || siteStyles[SiteStyleIds.LIGHT];
}

export function getSiteStyleColors() {
  return getActiveSiteStyle().colors;
}

// Apply SiteStyles colors to CSS variables for global UI usage
export function applySiteStylesToDocument() {
  try {
    const colors = getSiteStyleColors();
    const root = document.documentElement;
    root.style.setProperty('--site-bg', colors.main);
    root.style.setProperty('--site-text', colors.text);
    root.style.setProperty('--site-border', colors.border);
    root.style.setProperty('--site-muted', colors.muted);
    root.style.setProperty('--site-sub', colors.sub);
    root.style.setProperty('--site-accent', colors.accent || '#007bff');
    root.style.setProperty('--site-accent-soft', colors.accentSoft || '#e7f1ff');
    root.style.setProperty('--site-on-accent', colors.onAccent || '#ffffff');
    // Sidebar/hover convenience vars (fallbacks to muted)
    root.style.setProperty('--site-sidebar-bg', colors.sidebarBg || colors.main);
    root.style.setProperty('--site-hover-bg', colors.hoverBg || colors.muted);
    // Canvas background variable for Konva/Three.js consumers
    root.style.setProperty('--site-canvas-bg', colors.main || '#ffffff');
  } catch (_) {}
}

export default {
  SiteStyleIds,
  getActiveSiteStyleId,
  setActiveSiteStyleId,
  getActiveSiteStyle,
  getSiteStyleColors,
  applySiteStylesToDocument,
};
